import React, { createContext, useContext, useState, useEffect } from 'react';
import { documentApi } from '../services/api';

// Create context
const AppContext = createContext();

// Custom hook to use the context
export const useAppContext = () => useContext(AppContext);

// Provider component
export const AppProvider = ({ children }) => {
  // State for topics and documents
  const [topics, setTopics] = useState([]);
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  // State for RAG strategies
  const [ragStrategies, setRagStrategies] = useState([
    { id: 'traditional', name: 'Traditional RAG' },
    { id: 'qdrant_native_hybrid', name: 'Qdrant Native Hybrid RAG' }
  ]);
  const [selectedStrategy, setSelectedStrategy] = useState('traditional');

  // Fetch topics on component mount
  useEffect(() => {
    const fetchTopics = async () => {
      setLoading(true);
      try {
        const response = await documentApi.getTopics();
        setTopics(response.topics || []);
        setError(null);
      } catch (err) {
        setError('Failed to fetch topics. Please try again later.');
        console.error('Error fetching topics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  // Fetch documents for a specific topic
  const fetchDocumentsByTopic = async (topicId) => {
    if (!topicId) return;
    
    setLoading(true);
    try {
      const response = await documentApi.getDocumentsByTopic(topicId);
      // Make sure we're handling the response correctly
      const documentsList = response.documents || [];
      
      setDocuments(prev => ({
        ...prev,
        [topicId]: documentsList
      }));
      setError(null);
      return documentsList;
    } catch (err) {
      const errorMessage = `Failed to fetch documents for topic ${topicId}. ${err.message || 'Please try again later.'}`;
      console.error(errorMessage, err);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Upload a document
  const uploadDocument = async (formData) => {
    setLoading(true);
    try {
      const response = await documentApi.uploadDocument(formData);
      // Refresh the documents for the topic
      if (response.topic_id) {
        await fetchDocumentsByTopic(response.topic_id);
        
        // If this is a new topic, refresh the topics list
        if (!topics.includes(response.topic_id)) {
          const topicsResponse = await documentApi.getTopics();
          setTopics(topicsResponse.topics || []);
        }
      }
      setError(null);
      return response;
    } catch (err) {
      setError('Failed to upload document. Please try again later.');
      console.error('Error uploading document:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    topics,
    documents,
    loading,
    error,
    selectedTopic,
    setSelectedTopic,
    fetchDocumentsByTopic,
    uploadDocument,
    ragStrategies,
    selectedStrategy,
    setSelectedStrategy
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;

